// Един запис на телефон. Общата транспозиция „ред на колона" би направила от
// десет колони карта от десет реда; тук първият ред казва какво е станало и
// как е свършило, вторият — кой и къде, третият — техническото.

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import type { AuditLogEntry } from "../../../../api/auditLogs";
import { EM_DASH } from "../../../../components/dataTable";
import {
  SLOW_MS,
  impactLabelKey,
  moduleLabelKey,
  outcomeLabelKey,
  outcomeOf,
} from "./auditTaxonomy";

const DOT_CLASS: Record<string, string> = {
  ok: "bg-ok-solid",
  failed: "bg-danger",
  rejected: "bg-brand-deep",
  error: "bg-danger",
};

export const AuditMobileCard = ({ entry }: { entry: AuditLogEntry }) => {
  const { t } = useTranslation();
  const outcome = outcomeOf(entry);
  const impactLabel =
    entry.impact === "money" || entry.impact === "settings"
      ? t(impactLabelKey(entry.impact))
      : null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={`size-[9px] shrink-0 rounded-full ${DOT_CLASS[outcome]}`}
        />
        <span className="min-w-0 flex-1 truncate text-[14.5px] font-bold leading-[1.2] text-ink">
          {entry.operation}
        </span>
        <span className="font-pos-mono text-[12px] font-semibold tabular-nums text-muted">
          {dayjs(entry.timestamp).format("DD.MM HH:mm:ss")}
        </span>
      </div>

      {outcome === "ok" ? null : (
        // Точката казва, че нещо не е наред; редът казва КАКВО. На телефон
        // няма колона „Резултат", в която да се прочете.
        <p className="text-[12px] font-semibold leading-[1.2] text-danger-ink">
          {t(outcomeLabelKey(outcome))}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-soft">
        <span className="truncate">{entry.user_name ?? EM_DASH}</span>
        <span aria-hidden>·</span>
        <span>{t(moduleLabelKey(entry.module))}</span>
        {entry.table_name ? (
          <>
            <span aria-hidden>·</span>
            <span>{t("auditLog.col.table")} {entry.table_name}</span>
          </>
        ) : null}
        {entry.bill_id !== null ? (
          <>
            <span aria-hidden>·</span>
            <span className="font-pos-mono tabular-nums">#{entry.bill_id}</span>
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-pos-mono text-[11.5px] font-medium text-muted">
        <span>{entry.method}</span>
        <span aria-hidden>·</span>
        <span className="tabular-nums">{entry.status_code}</span>
        <span aria-hidden>·</span>
        <span
          className={`tabular-nums ${
            entry.duration_ms > SLOW_MS ? "text-brand-deep-ink" : ""
          }`}
        >
          {entry.duration_ms} ms
        </span>
        {impactLabel ? (
          <span className="rounded-md bg-brand-tint px-1.5 py-[1px] font-pos-sans text-[10.5px] font-bold text-brand-deep-ink">
            {impactLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
};
